// src/components/HomeHero.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import videoSmMd from '../assets/Japan/0710.mp4';
import videoLg from '../assets/Japan/0710.mp4';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: "easeOut" }
    }
};



const HomeHero = () => {
    const { t } = useTranslation('hero');

    return (
        <section className="relative flex min-h-screen w-full items-center overflow-hidden bg-[#07111f]">
            {/* Background Videos */}
            <video
                className="absolute inset-0 w-full h-full object-cover lg:hidden"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src={videoSmMd} type="video/mp4" />
            </video>

            <video
                className="absolute inset-0 w-full h-full object-cover hidden lg:block"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src={videoLg} type="video/mp4" />
            </video>

            {/* Overlay - more cinematic */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#07111f]/90 via-[#07111f]/75 to-[#07111f]/40" />

            {/* Content */}
            <div className="relative z-10 w-full px-5 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-16 md:py-20 lg:py-0">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto flex flex-col items-center lg:items-start text-center lg:text-left"
                >


                    {/* Main Title */}
                    <motion.h1
                        variants={itemVariants}
                        className="font-serif text-4xl font-semibold leading-[0.95] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
                    >
                        <span className="block">{t('title.line1')}</span>
                        <span className="mt-1 block bg-gradient-to-r from-[#7fd3ff] to-[#f7d774] bg-clip-text text-transparent md:mt-3">
                            {t('title.line2')}
                        </span>
                        <span className="mt-3 block text-3xl font-light italic text-[#7fd3ff]/90 sm:text-4xl md:mt-6 md:text-5xl lg:text-6xl">
                            {t('title.line3')}
                        </span>
                    </motion.h1>

                    {/* Subtitle */}
                    <motion.p
                        variants={itemVariants}
                        className="mt-8 max-w-3xl text-lg font-light leading-relaxed text-white/90 sm:text-xl md:mt-10 md:text-2xl lg:text-3xl"
                    >
                        {t('subtitle')}
                        <span className="ml-1 font-semibold text-[#7fd3ff]">{t('subtitle2')}</span>
                    </motion.p>

                    {/* CTA */}
                    <motion.div
                        variants={itemVariants}
                        className="mt-10 md:mt-14"
                    >
                        <a
                            href="/tour"
                            className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-[#7fd3ff]/30 bg-gradient-to-r from-[#7fd3ff] to-[#f7d774] px-8 py-4 text-lg font-semibold tracking-wide text-white shadow-[0_20px_50px_rgba(0,0,0,0.25)] transition-all duration-500 hover:opacity-90 sm:px-10 sm:py-5 sm:text-xl"
                        >
                            <span className="relative z-10">{t('cta')}</span>

                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent 
                             -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

                            <svg className="w-6 h-6 relative z-10 group-hover:translate-x-1.5 transition-transform"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </a>
                    </motion.div>

                    {/* Trust line */}
                    <motion.p
                        variants={itemVariants}
                        className="mt-10 text-base font-medium tracking-[0.25em] text-white/70 sm:text-lg"
                    >
                        {t('trust')}
                    </motion.p>
                </motion.div>
            </div>

            {/* Scroll Indicator - cleaner version */}
            <div className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-[0.35em] text-[#f7d774]">
                    Scroll
                </span>
                <div className="flex h-10 w-6 items-center justify-center rounded-full border-2 border-[#7fd3ff]/40">
                    <div className="h-3 w-1.5 animate-bounce rounded-full bg-[#7fd3ff]/70"></div>
                </div>
            </div>
        </section>
    );
};

export default HomeHero;